import { SlideAnnotation, UserName } from "pwapi";

import {
  Annotation,
  AnnotationType,
  AreaCountAnnotation,
  MeasurementAnnotation,
  PenDrawingAnnotation,
  RegionOfInterestAnnotation,
  ServerAnnotation,
  TextAnnotation,
  XYPoint,
} from "custom-models/ViewerTypes";

function createAnnotation(
  id: number | undefined,
  name: string,
  user: UserName,
  created_at: string | null,
  type: AnnotationType
): Annotation {
  return {
    id,
    name,
    user,
    created_at: created_at || new Date().toISOString(),
    type,
  } as Annotation;
}

export function createMeasurementAnnotation(
  id: number | undefined,
  name: string,
  user: UserName,
  created_at: string | null,
  point1: XYPoint | null = null,
  point2: XYPoint | null = null,
  label: string = ""
): MeasurementAnnotation {
  return {
    ...createAnnotation(id, name, user, created_at, AnnotationType.MEASUREMENT),
    point1,
    point2,
    label,
  } as MeasurementAnnotation;
}

export function createPenDrawingAnnotation(
  id: number | undefined,
  name: string,
  user: UserName,
  created_at: string | null,
  pathInfo: string,
  is_diagnostic: boolean
): PenDrawingAnnotation {
  return {
    ...createAnnotation(id, name, user, created_at, AnnotationType.PEN_DRAWING),
    pathInfo,
    is_diagnostic,
  } as PenDrawingAnnotation;
}

export function moveTo(
  annotation: PenDrawingAnnotation,
  point: XYPoint
): PenDrawingAnnotation {
  return pathOp(annotation, point, "M");
}

export function lineTo(
  annotation: PenDrawingAnnotation,
  point: XYPoint
): PenDrawingAnnotation {
  return pathOp(annotation, point, "L");
}

function pathOp(
  annotation: PenDrawingAnnotation,
  point: XYPoint,
  letter: string
): PenDrawingAnnotation {
  return createPenDrawingAnnotation(
    annotation.id,
    annotation.name,
    annotation.user,
    annotation.created_at,
    `${annotation.pathInfo} ${letter} ${Math.round(point.x)} ${Math.round(
      point.y
    )}`,
    annotation.is_diagnostic
  );
}

export function createTextAnnotation(
  id: number | undefined,
  name: string,
  user: UserName,
  created_at: string | null,
  point: XYPoint | null = null,
  label: string = ""
): TextAnnotation {
  return {
    ...createAnnotation(id, name, user, created_at, AnnotationType.TEXT),
    point,
    label,
  } as TextAnnotation;
}

export function createAreaCountAnnotation(
  id: number | undefined,
  name: string,
  user: UserName,
  created_at: string | null,
  point: XYPoint | null = null,
  label: string = ""
): AreaCountAnnotation {
  return {
    ...createAnnotation(id, name, user, created_at, AnnotationType.AREA_COUNT),
    point,
    label,
  } as AreaCountAnnotation;
}

export function createRegionOfInterestAnnotation(
  id: number | undefined,
  name: string,
  user: UserName,
  created_at: string | null,
  points: Array<XYPoint> = [],
  lesionLineType: string,
  subtype?: string
): RegionOfInterestAnnotation {
  return {
    ...createAnnotation(
      id,
      name,
      user,
      created_at,
      AnnotationType.REGION_OF_INTEREST
    ),
    points,
    lesionLineType,
    subtype,
  } as RegionOfInterestAnnotation;
}

export function extendRegionOfInterestAnnotation(
  annotation: RegionOfInterestAnnotation,
  point: XYPoint
): RegionOfInterestAnnotation {
  return {
    ...annotation,
    points: annotation.points.concat([point]),
  } as RegionOfInterestAnnotation;
}

export function toAnnotationRecord(annotation: Annotation): SlideAnnotation {
  const record: SlideAnnotation = {
    id: annotation.id,
    type: annotation.type as number,
    user: annotation.user,
    created_at: annotation.created_at,
  };
  if (annotation.name) {
    record.name = annotation.name;
  }

  if (annotation.type === AnnotationType.MEASUREMENT) {
    const measurementAnnotation = annotation as MeasurementAnnotation;
    record.is_diagnostic = false;
    if (measurementAnnotation.point1 && measurementAnnotation.point2) {
      const p1: XYPoint = {
        x: measurementAnnotation.point1.x,
        y: measurementAnnotation.point1.y,
      };
      const p2: XYPoint = {
        x: measurementAnnotation.point2.x,
        y: measurementAnnotation.point2.y,
      };
      record.points_list = JSON.stringify([p1, p2]);
    }
    if (measurementAnnotation.label) {
      record.label = measurementAnnotation.label;
    }
  }

  if (annotation.type === AnnotationType.PEN_DRAWING) {
    const penDrawingAnnotation = annotation as PenDrawingAnnotation;
    record.is_diagnostic = penDrawingAnnotation.is_diagnostic;
    record.svg_data = penDrawingAnnotation.pathInfo;
  }

  if (annotation.type === AnnotationType.TEXT) {
    const textAnnotation = annotation as TextAnnotation;
    record.is_diagnostic = false;
    if (textAnnotation.point) {
      record.points_list = JSON.stringify([textAnnotation.point]);
    }
    if (textAnnotation.label) {
      record.label = textAnnotation.label;
    }
  }

  if (annotation.type === AnnotationType.AREA_COUNT) {
    const areaCountAnnotation = annotation as AreaCountAnnotation;
    record.is_diagnostic = false;
    if (areaCountAnnotation.point) {
      record.points_list = JSON.stringify([areaCountAnnotation.point]);
    }
    if (areaCountAnnotation.label) {
      record.label = areaCountAnnotation.label;
    }
  }

  if (annotation.type === AnnotationType.REGION_OF_INTEREST) {
    const roiAnnotation = annotation as RegionOfInterestAnnotation;
    record.points_list = JSON.stringify(roiAnnotation.points);
    record.lesion_line =
      roiAnnotation.lesionLineType as SlideAnnotation.lesion_line;
    record.subtype = roiAnnotation.subtype as SlideAnnotation.subtype;
    if (roiAnnotation.label) {
      record.label = roiAnnotation.label;
    }
  }

  return record;
}

export const parseAnnotationRecord = (
  annotation: ServerAnnotation
): Annotation | undefined => {
  const {
    id = undefined,
    name,
    label = "",
    type,
    is_diagnostic,
    svg_data,
    points_list,
    lesion_line,
    subtype,
    point,
    point1,
    point2,
    user,
    created_at,
  } = annotation;
  if (type !== undefined && user !== undefined) {
    switch (type) {
      case AnnotationType.MEASUREMENT: {
        let startPoint: XYPoint | null = null;
        let endPoint: XYPoint | null = null;
        if (point1 && point2) {
          startPoint = point1 as XYPoint;
          endPoint = point2 as XYPoint;
        } else if (points_list) {
          [startPoint, endPoint] = JSON.parse(points_list);
        }
        return createMeasurementAnnotation(
          id,
          name || "",
          user,
          created_at,
          startPoint,
          endPoint,
          label
        );
      }
      case AnnotationType.PEN_DRAWING: {
        return createPenDrawingAnnotation(
          id,
          name || "",
          user,
          created_at,
          svg_data,
          is_diagnostic
        );
      }
      case AnnotationType.AREA_COUNT: {
        let singlePoint: XYPoint | null = null;
        if (point) {
          singlePoint = point;
        } else if (points_list) {
          [singlePoint] = JSON.parse(points_list);
        }
        return createAreaCountAnnotation(
          id,
          name || "",
          user,
          created_at,
          singlePoint,
          label
        );
      }
      case AnnotationType.TEXT: {
        let singlePoint: XYPoint | null = null;
        if (point) {
          singlePoint = point;
        } else if (points_list) {
          [singlePoint] = JSON.parse(points_list);
        }
        return createTextAnnotation(
          id,
          name || "",
          user,
          created_at,
          singlePoint,
          label
        );
      }
      case AnnotationType.REGION_OF_INTEREST: {
        return createRegionOfInterestAnnotation(
          id,
          name || "",
          user,
          created_at,
          JSON.parse(points_list),
          lesion_line,
          subtype
        );
      }
    }
  }
  return undefined;
};

/**
 * Uses created_at as a test for object identity
 * @param newPenDrawing
 * @param pendingAnnotationsBuffer
 */
export const notFound = (
  pendingAnnotationsBuffer: Annotation[],
  newPenDrawing: Annotation
): boolean =>
  pendingAnnotationsBuffer.find(
    (e) => e.created_at === newPenDrawing.created_at
  ) === undefined;
